// Experimentell alternativ rendering av <Score>, som SVG istället för
// Bulma/HTML-komponenter. Score.js lämnas helt orörd (bara två redan
// existerande funktioner har fått "export" tillagt så de kan återanvändas
// härifrån utan att dupliceras) - detta är ett fristående försök som kan
// användas som drop-in-ersättare där man vill prova det, utan att påverka
// den befintliga, verifierade renderingen någon annanstans.
//
// Innehållstolkningen (regex-parsning av roman numerals, key-transponering,
// scoremarks m.m.) är medvetet duplicerad från score.js snarare än utbruten
// till en delad modul - den koden är tät sammanflätad och risken med att
// refaktorera den gemensamt bedömdes högre än kostnaden av lite duplicering
// för ett första försök.

import { Interval, Note, Progression } from '@tonaljs/tonal';
import React, { useState } from 'react';
import { changeKeyIndex, getCurrentKey, getKeyIndex, getKeyTransposedBySemitones } from '../lib/transposeKey';
import { getBackgroundColor, translateChordFunction } from './score';

import _ from 'lodash';
import { useGlobalState } from '../state';

// ------------------------------------------------------
// Layout
// ------------------------------------------------------
const BARS_PER_LINE = 4;
const BAR_WIDTH = 200;
const LINE_WIDTH = BAR_WIDTH * BARS_PER_LINE;
const TOP_PAD = 32;   // rum för sektionsbokstav + "above"-text
const CHORD_ROW_H = 44;
const BOTTOM_PAD = 22; // rum för "below"-text
const LINE_HEIGHT = TOP_PAD + CHORD_ROW_H + BOTTOM_PAD;
const OUTER_PAD = 18;

const TEXT_COLOR = '#4d4d4d';
const ERROR_COLOR = '#ff3860';
const ARROW_COLOR = '#3273dc';
const BARLINE_COLOR = '#2b2b2b';
const HIGHLIGHT_COLOR = '#ff8800';

const markTextRegex = /C?:(?<C>[\w\s]+)(?=Bb)|Bb?:(?<Bb>[\w\s]+)(?=Eb)|Eb?:(?<Eb>[\w\s]+)>/gm;
const partChars = ['🄰', '🄱', '🄲', '🄳', '🄴', '🅂'];
const partCharLetters = { '🄰': 'A', '🄱': 'B', '🄲': 'C', '🄳': 'D', '🄴': 'E', '🅂': 'S' };

// ------------------------------------------------------
// Innehållstolkning: children + props -> lines (array av rader, varje rad en
// array av upp till 4 takt-strängar). Samma logik som score.js.
// ------------------------------------------------------
function parseScoreLines(children, props) {
  const format = props.format === undefined ? 'key' : props.format;
  const parentKeyTonic = props.t;
  const parentIsMajorQuality = props.q === 'm' ? false : true;
  let previousIsMajor = parentIsMajorQuality;
  const scoremarks = props.scoremarks;

  let contentOrg = '';
  const re = /(\r\n|\n|\r)/gm;
  const parts = /(\$[A-ES])/gm;
  React.Children.forEach(children, child => {
    if (typeof child === 'string') {
      child = child.replace(re, '');
      child = child.replace(parts, x => {
        switch (x.trim()) {
          case '$A': return '🄰';
          case '$B': return '🄱';
          case '$C': return '🄲';
          case '$D': return '🄳';
          case '$E': return '🄴';
          case '$S': return '🅂';
          default: return x;
        }
      });
      if (format === 'text') {
        contentOrg += child;
      } else {
        contentOrg += child.replace(/(^|['"\s🄰🄱🄲🄳🄴🅂]{1})([A-G])/gm, '$1!$2');
      }
    }
  });

  if (scoremarks) {
    const bars = contentOrg.split(/;|\|/);
    _.forEach(scoremarks, value => {
      const pos = value.pos.split(':');
      let bar = bars[pos[0] - 1];
      if (!bar) return false;
      const barchords = bar.split(',');
      let chord = barchords[pos[1] - 1];
      if (!chord) return false;
      if (value.a) {
        chord = chord.replace(/'.*'/, '');
        chord = format !== 'text' ? `${chord} '${value.a.replace(/([A-G])/gm, '!$1')} '` : `${chord} '${value.a} '`;
      }
      if (value.b) {
        chord = chord.replace(/".*"/, '');
        chord = format !== 'text' ? `${chord} "${value.b.replace(/([A-G])/gm, '!$1')}"` : `${chord} "${value.b}"`;
      }
      barchords[pos[1] - 1] = chord;
      bars[pos[0] - 1] = barchords.join(',');
    });
    contentOrg = bars.join(';');
  }
  let content = contentOrg;

  if (format === 'interval') {
    let currentKeyIndex = getKeyIndex(parentKeyTonic, parentIsMajorQuality);
    const keysRegex = /(?<t>(?:\[t)|(?:\[-?\b(?:[1-7])[PmMd]{1}(?<q>[mM]?)\b))(.*?)(?=\[|$)/g;
    const keysMatches = [...contentOrg.matchAll(keysRegex)];
    const romanChordsRegex = /((I{1,3}|IV|VI{0,2}){1}[#b]?)(\S+)?([|;,]|$|\s)/g;
    const keys = _.map(keysMatches, key => {
      let orgKeyContent = key[3];
      const isMajor = key[2] === 'm' ? false : true;
      const romanChordsMatches = [...orgKeyContent.matchAll(romanChordsRegex)];
      const romanChords = _.map(romanChordsMatches, item => item[1] + (item[3] || ''));
      const interval = key[1].substring(1);
      if (interval === 't') {
        currentKeyIndex = getKeyIndex(parentKeyTonic, parentIsMajorQuality);
        previousIsMajor = parentIsMajorQuality;
      } else {
        currentKeyIndex = changeKeyIndex(interval, currentKeyIndex);
      }
      if (isMajor && !previousIsMajor) currentKeyIndex -= 3;
      if (!isMajor && previousIsMajor) currentKeyIndex += 3;
      const chords = Progression.fromRomanNumerals(getCurrentKey(currentKeyIndex, isMajor), romanChords);
      previousIsMajor = isMajor;
      orgKeyContent = orgKeyContent.replace(/[A-G]/gm, '');
      return orgKeyContent.replace(romanChordsRegex, (match, p1, p2, p3, p4) => chords.shift() + p4);
    });
    content = contentOrg.replace(keysRegex, () => keys.shift());
  }

  if (format === 'key') {
    const scoreKeyRegEx = /([A-G]{1}(?:[#b]?))(m?)/;
    let scoreKeyMatch;
    if (typeof props.k === 'string' && ['A', 'B', 'C', 'D', 'E', 'F', 'G'].includes(props.k.charAt(0))) {
      scoreKeyMatch = props.k?.match(scoreKeyRegEx);
    }
    let scoreKey = 'C';
    if (scoreKeyMatch !== undefined) {
      scoreKey = scoreKeyMatch[1];
    } else {
      return { lines: null, scoremarks, error: 'Verify score definition. Missing score attribute k="".' };
    }

    const scoreOffset = Interval.semitones(Interval.distance(scoreKey, parentKeyTonic));
    const keysRegex = /(?<t>(?:\[)[A-G](?:[#b]?))(m?)(.*?)(?=\[|$)/g;
    const keysMatches = [...contentOrg.matchAll(keysRegex)];
    const romanChordsRegex = /((I{1,3}|IV|VI{0,2}){1}[#b]?)(\S+)?([|;,]|$|\s)/g;
    const keys = _.map(keysMatches, key => {
      const orgKeyContent = key[3];
      const isMajor = key[2] === 'm' ? false : true;
      const romanChordsMatches = [...orgKeyContent.matchAll(romanChordsRegex)];
      let romanChords = _.map(romanChordsMatches, item => item[1] + (item[3] || ''));
      romanChords = _.compact(romanChords);
      const currentKey = key[1].substring(1);
      const transposedKey = getKeyTransposedBySemitones(currentKey, isMajor, scoreOffset);
      let chords = Progression.fromRomanNumerals(transposedKey, romanChords);
      chords = _.map(chords, chord => {
        chord = chord.replace('#b', '').replace('b#', '');
        const found = [...chord.matchAll(/([ABCDEFGH])(#{2}|b{2})(.*)/gm)];
        if (!found[0]) return chord;
        const t = `${found[0][1]}${found[0][2]}`;
        return Note.simplify(t) + found[0][3];
      });
      return orgKeyContent.replace(romanChordsRegex, (match, p1, p2, p3, p4) => chords.shift() + p4);
    });
    content = contentOrg.replace(keysRegex, () => keys.shift());
  }

  let defs = null;
  if (content) {
    defs = content.split(/;|\|/);
  }
  if (defs && defs.length > 0 && defs[0].trim() === '🄰') {
    defs[1] = `${defs[0]} ${defs[1]}`;
    defs.shift();
  }
  if (defs && defs.length > 0 && defs[0] === '') {
    defs.shift();
  }
  if (defs && defs[defs.length - 1] === '') {
    defs.pop();
  }
  if (!defs || defs.length === 0) {
    return { lines: null, scoremarks, error: "Verify score definition. Don't use blank lines within <score>-tags." };
  }

  const lines = [];
  for (let i = 0; i < defs.length; i += BARS_PER_LINE) {
    lines.push(defs.slice(i, i + BARS_PER_LINE));
  }
  return { lines, scoremarks, error: null };
}

// ------------------------------------------------------
// Per-ackord: tolka en enskild takt-sträng till visningsdata
// ------------------------------------------------------
function parseChordName(rawName, { scoremarks, barindex, chordindex, currentInstrument }) {
  let name = rawName;

  let partChar = null;
  if (partChars.some(substr => name.startsWith(substr))) {
    partChar = name.substring(0, '🄰'.length);
    name = name.substring('🄰'.length);
  }

  name = name.trim();

  let errorIndication = '';
  if (!['!', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'X', 'x', '%'].includes(name.charAt(0))) {
    errorIndication = '!';
  }
  name = errorIndication + name.charAt(0).toUpperCase() + name.slice(1);

  if (['bb', '##'].includes(name.substring(1, 3))) {
    name = Note.simplify(name.substring(0, 3)) + name.substring(3);
  }
  if (['#b', 'b#'].includes(name.substring(1, 3))) {
    name = name.substring(0, 1) + name.substring(3);
  }

  const chordPosition = `${barindex + 1}:${chordindex + 1}`;
  const mark = _.find(scoremarks, { pos: chordPosition });

  let hasTo = false;
  name = name.replace(/(->)/g, () => {
    hasTo = true;
    return '';
  });
  const leadsto = mark?.to || hasTo;

  let bc = '';
  name = name.replace(/c:([rgbcmy])/g, (whole, code) => {
    bc = code;
    return '';
  });
  const markColor = getBackgroundColor(mark?.bc || bc);

  let above = '';
  name = name.replace(/'(.+?)'/g, (whole, text) => {
    above = text;
    return '';
  });
  for (const m of above.matchAll(markTextRegex)) {
    if (m.groups[currentInstrument]) {
      above = above.replace(/<(.*?)>/, m.groups[currentInstrument]);
    }
  }
  above = translateChordFunction(above);

  let below = '';
  name = name.replace(/"(.+?)"/g, (whole, text) => {
    below = text;
    return '';
  });
  for (const m of below.matchAll(markTextRegex)) {
    if (m.groups[currentInstrument]) {
      below = below.replace(/<(.*?)>/, m.groups[currentInstrument]);
    }
  }

  const isRepeat = name.trim() === '%';
  const isEmpty = name.trim() === '!' || name.trim() === '';
  const isSpacer = name.trim().toUpperCase() === 'X';

  name = name
    .replace('X', '')
    .replace(/b/g, '♭')
    .replace(/#/g, '♯')
    .replace('maj', 'Δ')
    .replace('o', '°')
    .replace('dim', '°')
    .trim();

  const isError = name.startsWith('!');

  let chordName = name;
  let chordExt = '';
  if (!isRepeat && !isEmpty && !isSpacer) {
    const rootMatch = name.match(/^(!?[ABCDEFG][♭♯]?)(.*)$/);
    if (rootMatch) {
      chordName = rootMatch[1];
      chordExt = rootMatch[2];
    }
  }

  return {
    chordPosition, mark, leadsto, markColor, above, below,
    isRepeat, isEmpty, isSpacer, isError, partChar,
    chordName, chordExt,
  };
}

// ------------------------------------------------------
// SVG-byggstenar
// ------------------------------------------------------
const RepeatGlyph = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`} stroke={TEXT_COLOR} strokeWidth={2.2} strokeLinecap="round" fill={TEXT_COLOR}>
    <line x1={-9} y1={9} x2={9} y2={-9} />
    <circle cx={-9} cy={-7} r={2.6} stroke="none" />
    <circle cx={9} cy={7} r={2.6} stroke="none" />
  </g>
);

const Arrow = ({ x, y }) => (
  <path
    d={`M ${x - 8} ${y - 4} L ${x + 4} ${y - 4} L ${x + 4} ${y - 8} L ${x + 12} ${y} L ${x + 4} ${y + 8} L ${x + 4} ${y + 4} L ${x - 8} ${y + 4} Z`}
    fill={ARROW_COLOR}
  />
);

const SectionBadge = ({ x, y, letter }) => (
  <g transform={`translate(${x}, ${y})`}>
    <rect x={0} y={-16} width={20} height={20} rx={4} fill="none" stroke={TEXT_COLOR} strokeWidth={1.4} />
    <text x={10} y={-2} textAnchor="middle" fontSize={13} fontWeight="700" fill={TEXT_COLOR}>{letter}</text>
  </g>
);

const SvgChord = ({ x, y, w, name, meta, isSelected, onClick, clickable }) => {
  const { markColor, above, below, leadsto, isRepeat, isEmpty, isSpacer, isError, chordName, chordExt } = meta;
  const cx = x + w / 2;
  const cy = y;

  return (
    <g
      onClick={clickable ? onClick : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}
    >
      {markColor && markColor !== 'white' && (
        <rect x={x + 2} y={y - CHORD_ROW_H / 2 + 2} width={w - 4} height={CHORD_ROW_H - 4} rx={4} fill={markColor} />
      )}
      {isSelected && (
        <rect x={x + 2} y={y - CHORD_ROW_H / 2 + 2} width={w - 4} height={CHORD_ROW_H - 4} rx={4} fill="none" stroke={HIGHLIGHT_COLOR} strokeWidth={2} />
      )}

      {above && (
        <text x={x + 6} y={y - CHORD_ROW_H / 2 - 4} fontSize={11} fill={TEXT_COLOR}>{above}</text>
      )}
      {below && (
        <text x={x + 6} y={y + CHORD_ROW_H / 2 + 14} fontSize={11} fill={TEXT_COLOR}>{below}</text>
      )}

      {isRepeat && <RepeatGlyph x={cx} y={cy} />}
      {isEmpty && !isRepeat && (
        <text x={x + 8} y={cy + 8} fontSize={22} fill={ERROR_COLOR}>!</text>
      )}
      {!isRepeat && !isEmpty && !isSpacer && (
        <text x={x + 8} y={cy + 9} fontSize={24} fontWeight="600" fill={isError ? ERROR_COLOR : TEXT_COLOR}>
          {chordName}
          {chordExt && <tspan dy={-9} fontSize={14} fontWeight="500">{chordExt}</tspan>}
        </text>
      )}

      {leadsto && <Arrow x={x + w - 16} y={cy - 12} />}
    </g>
  );
};

const SvgBar = ({ x, y, chordsStr, barindex, isFirstBarOfScore, isEndOfLine, scoremarks, currentInstrument, events }) => {
  const chs = chordsStr.split(',');
  const beatW = BAR_WIDTH / (chs.length || 1);

  return (
    <g>
      {/* Taktstreck */}
      <line x1={x} y1={y - CHORD_ROW_H / 2 - 6} x2={x} y2={y + CHORD_ROW_H / 2 + 6} stroke={BARLINE_COLOR} strokeWidth={isFirstBarOfScore ? 2 : 1.4} />
      {isFirstBarOfScore && (
        <line x1={x + 4} y1={y - CHORD_ROW_H / 2 - 6} x2={x + 4} y2={y + CHORD_ROW_H / 2 + 6} stroke={BARLINE_COLOR} strokeWidth={2} />
      )}
      {isEndOfLine && (
        <line x1={x + BAR_WIDTH} y1={y - CHORD_ROW_H / 2 - 6} x2={x + BAR_WIDTH} y2={y + CHORD_ROW_H / 2 + 6} stroke={BARLINE_COLOR} strokeWidth={1.4} />
      )}

      {chs.map((ch, index) => {
        const raw = ch.trim() === '' && index === 0 ? '!' : ch;
        const meta = parseChordName(raw, { scoremarks, barindex, chordindex: index, currentInstrument });
        const clickable = !!events?.onChordClick;
        return (
          <SvgChord
            key={index}
            x={x + beatW * index}
            y={y}
            w={beatW}
            name={raw}
            meta={meta}
            isSelected={meta.chordPosition === events?.selectedChordPos}
            clickable={clickable}
            onClick={() => {
              events?.setSelectedChordPos(meta.chordPosition);
              events?.onChordClick({ chord: [meta.chordName + meta.chordExt], pos: meta.chordPosition });
            }}
          />
        );
      })}
    </g>
  );
};

const SvgLine = ({ bars, lineIndex, top, scoremarks, currentInstrument, events }) => {
  const y = top + TOP_PAD + CHORD_ROW_H / 2;
  const firstMeta = bars.length > 0
    ? parseChordName(bars[0].split(',')[0] || '', { scoremarks, barindex: lineIndex * BARS_PER_LINE, chordindex: 0, currentInstrument })
    : null;

  return (
    <g>
      {firstMeta?.partChar && <SectionBadge x={0} y={top + TOP_PAD - 12} letter={partCharLetters[firstMeta.partChar] || '?'} />}
      {bars.map((bar, index) => (
        <SvgBar
          key={index}
          x={index * BAR_WIDTH}
          y={y}
          chordsStr={bar}
          barindex={(lineIndex * BARS_PER_LINE) + index}
          isFirstBarOfScore={lineIndex === 0 && index === 0}
          isEndOfLine={index === bars.length - 1}
          scoremarks={scoremarks}
          currentInstrument={currentInstrument}
          events={events}
        />
      ))}
    </g>
  );
};

// ------------------------------------------------------
// Komponent
// ------------------------------------------------------
const ScoreSvg = ({ children, ...props }) => {
  const [selectedChordPos, setSelectedChordPos] = useState('');
  const [currentInstrument] = useGlobalState('instrument');
  const events = { onChordClick: props.onChordClick, selectedChordPos, setSelectedChordPos };

  const { lines, scoremarks, error } = parseScoreLines(children, props);

  if (error) {
    return <div style={{ color: '#ff3860', padding: '1em' }}>{error}</div>;
  }

  const height = lines.length * LINE_HEIGHT + OUTER_PAD * 2;

  return (
    <div style={{ background: 'white', borderRadius: 6, padding: '0.5em', marginTop: 10, marginBottom: 10 }}>
      <svg
        viewBox={`0 0 ${LINE_WIDTH} ${height}`}
        width="100%"
        height="auto"
        role="img"
        style={{ display: 'block', fontFamily: 'Helvetica, Arial, sans-serif' }}
      >
        {lines.map((bars, index) => (
          bars.length > 0
            ? (
              <SvgLine
                key={index}
                bars={bars}
                lineIndex={index}
                top={OUTER_PAD + index * LINE_HEIGHT}
                scoremarks={scoremarks}
                currentInstrument={currentInstrument}
                events={events}
              />
            )
            : null
        ))}
      </svg>
    </div>
  );
};

export default ScoreSvg;
